import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import {
  fetchAdUnits,
  createAdUnit,
  updateAdUnit,
  deleteAdUnit,
  bulkUpdateAdUnits,
  fetchAdUnitStatistics,
} from "../store/slices/adUnitSlice";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  StopIcon,
  ChartBarIcon,
  CursorArrowRaysIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  ArrowsPointingOutIcon,
} from "@heroicons/react/24/outline";

interface AdUnitFormData {
  name: string;
  ad_type: "display" | "video" | "native" | "search" | "responsive";
  size: string;
  description?: string;
  ad_code?: any;
  settings?: any;
}

interface AdUnit {
  id: number;
  name: string;
  description?: string;
  ad_type: string;
  size: string;
  status: string;
  revenue: number;
  ctr: number | string;
  clicks: number;
  impressions: number;
  created_at: string;
  [key: string]: any;
}

interface Statistics {
  revenue_by_type?: {
    [key: string]: {
      count: number;
      revenue: number;
      avg_ctr: number;
    };
  };
}

const AdUnits: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { adUnits, summary, statistics, isLoading, error } = useSelector(
    (state: RootState) => state.adUnits
  );

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedAdUnit, setSelectedAdUnit] = useState<AdUnit | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState<string>("revenue");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);

  const [newAdUnit, setNewAdUnit] = useState<AdUnitFormData>({
    name: "",
    ad_type: "display",
    size: "728x90",
    description: "",
    ad_code: {
      code: '<div class="ad-unit" data-type="display"></div>',
      placement: "auto",
    },
    settings: {
      auto_refresh: false,
      frequency_cap: 5,
      targeting: { all: true },
    },
  });

  const adTypeSizes = {
    display: [
      { value: "728x90", label: "Leaderboard (728x90)" },
      { value: "300x250", label: "Medium Rectangle (300x250)" },
      { value: "160x600", label: "Wide Skyscraper (160x600)" },
      { value: "320x50", label: "Mobile Banner (320x50)" },
      { value: "970x250", label: "Billboard (970x250)" },
      { value: "300x600", label: "Half Page (300x600)" },
    ],
    video: [
      { value: "640x360", label: "640x360" },
      { value: "1280x720", label: "1280x720" },
      { value: "1920x1080", label: "1920x1080" },
    ],
    native: [{ value: "flexible", label: "Flexible" }],
    search: [{ value: "text", label: "Text" }],
    responsive: [{ value: "responsive", label: "Responsive" }],
  };

  useEffect(() => {
    dispatch(fetchAdUnits({}));
    dispatch(fetchAdUnitStatistics());
  }, [dispatch]);

  // Helper function to safely get CTR as number
  const getCTR = (unit: AdUnit): number => {
    const ctr = unit.ctr;
    if (typeof ctr === "number") return ctr;
    if (typeof ctr === "string") {
      const parsed = parseFloat(ctr);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  // Helper function to safely format CTR for display
  const formatCTR = (unit: AdUnit): string => {
    const ctr = getCTR(unit);
    return ctr.toFixed(2);
  };

  const filteredAdUnits = adUnits
    .filter((unit: AdUnit) => {
      if (statusFilter !== "all" && unit.status !== statusFilter) return false;
      if (typeFilter !== "all" && unit.ad_type !== typeFilter) return false;
      if (
        searchTerm &&
        !unit.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
        return false;
      return true;
    })
    .sort((a: AdUnit, b: AdUnit) => {
      let aValue: any, bValue: any;

      if (sortBy === "ctr") {
        aValue = getCTR(a);
        bValue = getCTR(b);
      } else if (
        sortBy === "revenue" ||
        sortBy === "clicks" ||
        sortBy === "impressions"
      ) {
        aValue = typeof a[sortBy] === "number" ? a[sortBy] : 0;
        bValue = typeof b[sortBy] === "number" ? b[sortBy] : 0;
      } else if (sortBy === "name") {
        aValue = a.name?.toLowerCase() || "";
        bValue = b.name?.toLowerCase() || "";
      } else if (sortBy === "created_at") {
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
      } else {
        aValue = a[sortBy] || 0;
        bValue = b[sortBy] || 0;
      }

      if (sortOrder === "desc") {
        return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    });

  const handleCreateAdUnit = async () => {
    try {
      await dispatch(createAdUnit(newAdUnit));
      setShowCreateForm(false);
      setNewAdUnit({
        name: "",
        ad_type: "display",
        size: "728x90",
        description: "",
        ad_code: {
          code: '<div class="ad-unit" data-type="display"></div>',
          placement: "auto",
        },
        settings: {
          auto_refresh: false,
          frequency_cap: 5,
          targeting: { all: true },
        },
      });
    } catch (error) {
      console.error("Failed to create ad unit:", error);
    }
  };

  const handleUpdateAdUnit = async () => {
    if (!selectedAdUnit) return;

    try {
      await dispatch(
        updateAdUnit({
          id: selectedAdUnit.id,
          data: selectedAdUnit,
        })
      );
      setShowEditForm(false);
      setSelectedAdUnit(null);
    } catch (error) {
      console.error("Failed to update ad unit:", error);
    }
  };

  const handleDeleteAdUnit = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this ad unit?")) {
      try {
        await dispatch(deleteAdUnit(id));
      } catch (error) {
        console.error("Failed to delete ad unit:", error);
      }
    }
  };

  const handleToggleStatus = async (unit: AdUnit) => {
    const newStatus = unit.status === "active" ? "paused" : "active";
    try {
      await dispatch(
        updateAdUnit({
          id: unit.id,
          data: { ...unit, status: newStatus },
        })
      );
    } catch (error) {
      console.error("Failed to toggle status:", error);
    }
  };

  const handleBulkUpdate = async (status: string) => {
    if (selectedIds.length === 0) return;

    try {
      await dispatch(bulkUpdateAdUnits({ ids: selectedIds, status }));
      setSelectedIds([]);
      setShowBulkActions(false);
    } catch (error) {
      console.error("Failed to bulk update:", error);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredAdUnits.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAdUnits.map((unit: AdUnit) => unit.id));
    }
  };

  const handleSelectAdUnit = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const getPerformanceColor = (ctr: number) => {
    if (ctr > 3) return "text-green-600";
    if (ctr > 1) return "text-yellow-600";
    return "text-red-600";
  };

  const getPerformanceIcon = (ctr: number) => {
    if (ctr > 3) return ChevronUpIcon;
    if (ctr > 1) return ChevronDownIcon;
    return ExclamationTriangleIcon;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  if (isLoading && adUnits.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-normal text-gray-900">My ads</h1>
              <p className="text-sm text-gray-600 mt-1">
                Create and manage your ad units
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  dispatch(fetchAdUnits({}));
                  dispatch(fetchAdUnitStatistics());
                }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
                title="Refresh"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center space-x-2 bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg"
              >
                <PlusIcon className="h-5 w-5" />
                <span>New ad unit</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mx-6 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total ad units</p>
                <p className="text-2xl font-normal text-gray-900">
                  {summary?.total || 0}
                </p>
              </div>
              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                <ChartBarIcon className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-normal text-gray-900">
                  {summary?.active || 0}
                </p>
              </div>
              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                <PlayIcon className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total revenue</p>
                <p className="text-2xl font-normal text-gray-900">
                  $
                  {typeof summary?.total_revenue === "number"
                    ? summary.total_revenue.toFixed(2)
                    : "0.00"}
                </p>
              </div>
              <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center">
                <CurrencyDollarIcon className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overall CTR</p>
                <p className="text-2xl font-normal text-gray-900">
                  {typeof summary?.overall_ctr === "number"
                    ? summary.overall_ctr.toFixed(2)
                    : "0.00"}
                  %
                </p>
              </div>
              <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                <CursorArrowRaysIcon className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <div className="px-6 py-2">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckIcon className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">
                  {selectedIds.length} ad unit
                  {selectedIds.length !== 1 ? "s" : ""} selected
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkUpdate("active")}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  Activate
                </button>
                <button
                  onClick={() => handleBulkUpdate("paused")}
                  className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                >
                  Pause
                </button>
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        `Are you sure you want to delete ${selectedIds.length} ad units?`
                      )
                    ) {
                      selectedIds.forEach((id) => handleDeleteAdUnit(id));
                      setSelectedIds([]);
                    }
                  }}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  Delete
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search ad units..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded text-sm w-48 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-4 w-4 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="all">All status</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="all">All types</option>
                <option value="display">Display</option>
                <option value="video">Video</option>
                <option value="native">Native</option>
                <option value="search">Search</option>
                <option value="responsive">Responsive</option>
              </select>

              {/* Statistics Button */}
              <button
                onClick={() => setShowStatistics(!showStatistics)}
                className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
              >
                <ArrowsPointingOutIcon className="h-4 w-4" />
                <span>Statistics</span>
              </button>
            </div>

            {/* Sort */}
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="revenue">Revenue</option>
                <option value="name">Name</option>
                <option value="ctr">CTR</option>
                <option value="clicks">Clicks</option>
                <option value="impressions">Impressions</option>
                <option value="created_at">Date Created</option>
              </select>
              <button
                onClick={() =>
                  setSortOrder(sortOrder === "desc" ? "asc" : "desc")
                }
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
              >
                {sortOrder === "desc" ? (
                  <ChevronDownIcon className="h-5 w-5" />
                ) : (
                  <ChevronUpIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Panel */}
      {showStatistics && statistics && (
        <div className="px-6 py-4">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-medium text-gray-900">
                  Ad Unit Statistics
                </h3>
                <p className="text-sm text-gray-600">Performance by ad type</p>
              </div>
              <button
                onClick={() => setShowStatistics(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {statistics.revenue_by_type &&
            Object.keys(statistics.revenue_by_type).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(statistics.revenue_by_type).map(
                  ([type, data]: [string, any]) => (
                    <div
                      key={type}
                      className="p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900 capitalize">
                          {type}
                        </span>
                        <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                          {data.count || 0} units
                        </span>
                      </div>
                      <p className="text-lg font-medium text-gray-900">
                        $
                        {typeof data.revenue === "number"
                          ? data.revenue.toFixed(2)
                          : "0.00"}
                      </p>
                      <p className="text-sm text-gray-600">Revenue</p>
                      <div className="mt-2 flex items-center space-x-2">
                        <span className="text-sm text-gray-600">CTR:</span>
                        <span className="text-sm font-medium text-gray-900">
                          {typeof data.avg_ctr === "number"
                            ? data.avg_ctr.toFixed(2)
                            : "0.00"}
                          %
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ChartBarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No statistics available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ad Units Table */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-1">
                <input
                  type="checkbox"
                  checked={
                    selectedIds.length === filteredAdUnits.length &&
                    filteredAdUnits.length > 0
                  }
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 rounded"
                />
              </div>
              <div className="col-span-4">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ad unit
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue
                </span>
              </div>
              <div className="col-span-2">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </span>
              </div>
              <div className="col-span-1">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </span>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200">
            {filteredAdUnits.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {adUnits.length === 0
                    ? "No ad units yet"
                    : "No matching ad units"}
                </h3>
                <p className="text-gray-600 mb-6">
                  {adUnits.length === 0
                    ? "Create your first ad unit to start earning"
                    : "Try adjusting your filters"}
                </p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create ad unit
                </button>
              </div>
            ) : (
              filteredAdUnits.map((unit: AdUnit) => {
                const ctrValue = getCTR(unit);
                const PerformanceIcon = getPerformanceIcon(ctrValue);
                return (
                  <div key={unit.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Checkbox */}
                      <div className="col-span-1">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(unit.id)}
                          onChange={() => handleSelectAdUnit(unit.id)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                      </div>

                      {/* Ad Unit Info */}
                      <div className="col-span-4">
                        <div className="flex items-start space-x-3">
                          <div className="h-10 w-10 bg-blue-100 rounded flex items-center justify-center flex-shrink-0">
                            <ChartBarIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {unit.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {unit.description || "No description"}
                            </p>
                            <div className="flex items-center space-x-3 mt-1">
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded capitalize">
                                {unit.ad_type}
                              </span>
                              <span className="text-xs text-gray-500">
                                {unit.size}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              Created {formatDate(unit.created_at)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Performance */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-2">
                          <PerformanceIcon
                            className={`h-4 w-4 ${getPerformanceColor(
                              ctrValue
                            )}`}
                          />
                          <div>
                            <p
                              className={`font-medium ${getPerformanceColor(
                                ctrValue
                              )}`}
                            >
                              {formatCTR(unit)}%
                            </p>
                            <p className="text-xs text-gray-500">
                              {typeof unit.clicks === "number"
                                ? unit.clicks.toLocaleString()
                                : "0"}{" "}
                              clicks
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Revenue */}
                      <div className="col-span-2">
                        <p className="font-medium text-gray-900">
                          $
                          {typeof unit.revenue === "number"
                            ? unit.revenue.toFixed(2)
                            : "0.00"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {typeof unit.impressions === "number"
                            ? unit.impressions.toLocaleString()
                            : "0"}{" "}
                          impressions
                        </p>
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-3">
                          <span
                            className={`text-xs px-3 py-1 rounded-full ${getStatusColor(
                              unit.status
                            )}`}
                          >
                            {unit.status}
                          </span>
                          <button
                            onClick={() => handleToggleStatus(unit)}
                            className={`p-1.5 rounded ${
                              unit.status === "active"
                                ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
                                : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                            }`}
                            title={
                              unit.status === "active" ? "Pause" : "Activate"
                            }
                          >
                            {unit.status === "active" ? (
                              <StopIcon className="h-4 w-4" />
                            ) : (
                              <PlayIcon className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="col-span-1">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedAdUnit(unit);
                              setShowEditForm(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteAdUnit(unit.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Performance Tips */}
      <div className="px-6 py-4">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <InformationCircleIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-2">
                Ad Unit Performance Tips
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-white/80 backdrop-blur-sm rounded border border-white/20">
                  <h4 className="font-medium text-gray-900 mb-1">
                    Placement matters
                  </h4>
                  <p className="text-sm text-gray-600">
                    Place ad units above the fold for better viewability
                  </p>
                </div>
                <div className="p-4 bg-white/80 backdrop-blur-sm rounded border border-white/20">
                  <h4 className="font-medium text-gray-900 mb-1">
                    Mobile optimization
                  </h4>
                  <p className="text-sm text-gray-600">
                    Use responsive ad units for better mobile performance
                  </p>
                </div>
              </div>
              <button className="mt-4 text-sm text-blue-600 hover:text-blue-700">
                View more optimization tips →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create Ad Unit Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Create new ad unit
                </h3>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={newAdUnit.name}
                    onChange={(e) =>
                      setNewAdUnit({ ...newAdUnit, name: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    placeholder="Enter ad unit name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ad Type
                  </label>
                  <select
                    value={newAdUnit.ad_type}
                    onChange={(e) => {
                      const type = e.target.value as AdUnitFormData["ad_type"];
                      setNewAdUnit({
                        ...newAdUnit,
                        ad_type: type,
                        size: adTypeSizes[type][0].value,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="display">Display</option>
                    <option value="video">Video</option>
                    <option value="native">Native</option>
                    <option value="search">Search</option>
                    <option value="responsive">Responsive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Size
                  </label>
                  <select
                    value={newAdUnit.size}
                    onChange={(e) =>
                      setNewAdUnit({ ...newAdUnit, size: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    {adTypeSizes[newAdUnit.ad_type].map((size) => (
                      <option key={size.value} value={size.value}>
                        {size.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={newAdUnit.description}
                    onChange={(e) =>
                      setNewAdUnit({
                        ...newAdUnit,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[80px]"
                    placeholder="Describe this ad unit..."
                  />
                </div>

                <div className="bg-blue-50 p-4 rounded">
                  <div className="flex items-start space-x-2">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                    <p className="text-sm text-blue-700">
                      This ad unit will be created with default settings. You
                      can customize it later.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAdUnit}
                    disabled={!newAdUnit.name}
                    className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${
                      !newAdUnit.name ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    Create ad unit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ad Unit Modal */}
      {showEditForm && selectedAdUnit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Edit ad unit
                </h3>
                <button
                  onClick={() => {
                    setShowEditForm(false);
                    setSelectedAdUnit(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={selectedAdUnit.name}
                    onChange={(e) =>
                      setSelectedAdUnit({
                        ...selectedAdUnit,
                        name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={selectedAdUnit.status}
                    onChange={(e) =>
                      setSelectedAdUnit({
                        ...selectedAdUnit,
                        status: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={selectedAdUnit.description || ""}
                    onChange={(e) =>
                      setSelectedAdUnit({
                        ...selectedAdUnit,
                        description: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[80px]"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowEditForm(false);
                      setSelectedAdUnit(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateAdUnit}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Save changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdUnits;
